
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

export type SearchResult = {
  id: string;
  name: string;
  family_id?: string | null;
};

export type SearchResults = {
  families: SearchResult[];
  students: SearchResult[];
  classes: SearchResult[];
  events: SearchResult[];
  staffMembers: SearchResult[];
};

export const useGlobalSearch = (searchQuery: string) => {
  const [results, setResults] = useState<SearchResults>({
    families: [],
    students: [],
    classes: [],
    events: [],
    staffMembers: []
  });
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const performSearch = async () => {
      if (!searchQuery || searchQuery.length < 2) {
        setResults({
          families: [],
          students: [],
          classes: [],
          events: [],
          staffMembers: []
        });
        return;
      }
      
      setIsLoading(true);
      
      try {
        const searchTerm = `%${searchQuery.toLowerCase()}%`;
        const organizationId = user?.user_metadata?.organization_id;
        
        if (!organizationId) {
          console.error("No organization ID found for the current user");
          setIsLoading(false);
          return;
        }

        // Search families
        const { data: families, error: familiesError } = await supabase
          .from('families')
          .select('id, name')
          .eq('organization_id', organizationId)
          .ilike('name', searchTerm)
          .limit(5);
          
        if (familiesError) throw familiesError;
        
        // Search students
        const { data: students, error: studentsError } = await supabase
          .from('students')
          .select('id, first_name, last_name, family_id')
          .eq('organization_id', organizationId)
          .or(`first_name.ilike.${searchTerm},last_name.ilike.${searchTerm}`)
          .limit(5);
          
        if (studentsError) throw studentsError;
        
        // Search classes
        const { data: classes, error: classesError } = await supabase
          .from('classes')
          .select('id, name, is_event')
          .eq('organization_id', organizationId)
          .ilike('name', searchTerm)
          .eq('is_event', false)
          .limit(5);
          
        if (classesError) throw classesError;
        
        // Search events
        const { data: events, error: eventsError } = await supabase
          .from('classes')
          .select('id, name')
          .eq('organization_id', organizationId)
          .ilike('name', searchTerm)
          .eq('is_event', true)
          .limit(5);
          
        if (eventsError) throw eventsError;

        // Search staff members - Fix: Using OR condition for both first and last name
        const { data: staffMembers, error: staffError } = await supabase
          .from('staff_members')
          .select('id, first_name, last_name')
          .eq('organization_id', organizationId)
          .or(`first_name.ilike.${searchTerm},last_name.ilike.${searchTerm}`)
          .limit(10); // Increased limit to catch more staff members

        if (staffError) throw staffError;
        
        console.log("Staff search results:", staffMembers);
        
        setResults({
          families: families || [],
          students: students ? students.map(student => ({
            id: student.id,
            name: `${student.first_name} ${student.last_name}`,
            family_id: student.family_id,
          })) : [],
          classes: classes || [],
          events: events || [],
          staffMembers: staffMembers ? staffMembers.map(staff => ({
            id: staff.id,
            name: `${staff.first_name} ${staff.last_name}`
          })) : []
        });
        
      } catch (error) {
        console.error("Search error:", error);
        setResults({
          families: [],
          students: [],
          classes: [],
          events: [],
          staffMembers: []
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    const timer = setTimeout(() => {
      performSearch();
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchQuery, user]);

  return {
    isLoading,
    results
  };
};
